import type { auth as AuthApi } from '@cwos/api-client';

/**
 * Admin-web API client. Every call uses `credentials: 'include'` so the opaque
 * `cwos_session` cookie flows automatically; the admin never holds or sends an
 * IdP token. Paths are the backend's exact route paths (mixed `/api` and
 * unprefixed families — the Vite dev proxy forwards them verbatim).
 *
 * Every call also carries `x-request-id`, matching @cwos/api-client's transport.
 * The API adopts an inbound one as its own request id (server.ts `genReqId`) and
 * puts it on every log line for the request, so a failure seen here can be found
 * there. Two clients, one convention — a request from this module was otherwise
 * the one kind that stayed untraceable.
 */
import { newRequestId } from '@cwos/api-client';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const requestId = newRequestId();
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { ...(init.headers as Record<string, string> | undefined), 'x-request-id': requestId },
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON body (e.g. empty 204) — leave body null
  }
  if (!res.ok) {
    const err = new Error(
      (body as { message?: string } | null)?.message ?? `request failed: ${res.status}`,
    ) as Error & { status?: number; body?: unknown; requestId?: string };
    err.status = res.status;
    err.body = body;
    // Read back rather than assumed: if something in the chain rewrote the
    // header, the value that reached the API is the one worth quoting.
    err.requestId = res.headers.get('x-request-id') || requestId;
    throw err;
  }
  return body as T;
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path);
}

/**
 * True when a failed request means "you are not signed in / not allowed", and
 * false when it means "we could not tell".
 *
 * The distinction is not cosmetic. Treating a 500 or a dropped connection as
 * "no session" signs an authenticated person out on screen while their session
 * cookie is still perfectly valid — they lose their place, and the product
 * looks as though it forgot them. Only the server saying 401/403 is evidence
 * about the session; everything else is evidence about the network.
 */
export function isUnauthorized(err: unknown): boolean {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  return status === 401 || status === 403;
}

export function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * THE SERVER'S `/auth/me`, NOT THIS APP'S IDEA OF IT.
 *
 * This file used to declare a second, structurally separate `Me` — eleven of
 * the canonical type's thirteen fields, retyped. It type-checked, because a
 * response type in TypeScript is a CLAIM about bytes and not a check on them,
 * so the two could disagree indefinitely without anything failing: the app was
 * validated against its own idea of the server rather than against the
 * server's.
 *
 * The two fields it dropped are the two that matter most and the two a private
 * copy is most likely to omit: `permissions` and `permissionScopes`. Every
 * permission-shaped decision in this app is made from them, and the session
 * principal this module hands to `auth.tsx` could not carry either — so the
 * one object that knows who is signed in was the one object that could not say
 * what they may do.
 *
 * `MembershipView` is re-exported for the same reason: the local declaration
 * spelled the membership shape inline, which is a third place for the org
 * switcher's fields to drift.
 */
export type Me = AuthApi.Me;
export type MembershipView = AuthApi.MembershipView;
export type PermissionGrant = AuthApi.PermissionGrant;

export interface LoginResult {
  userId: string;
  orgId: string;
  authStrength: string;
  expiresAt: string;
}
